import { mimeTypes as globalMimeTypes } from 'client/constants/editorSettings'
import { DataFile } from 'client/store/files/filesReducer'
import React, { memo, useEffect, useMemo } from 'react'
import { DropTargetMonitor, useDrop } from 'react-dnd'
import { TextButton } from 'src/editor/shared/ui-lib/buttons'
import { CheckboxNew } from 'src/editor/shared/ui-lib/checkbox-new'
import { Tooltip } from 'src/shared/components/tooltip'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { twJoin } from 'tailwind-merge'
import { useFileUploader } from './hooks/use-file-uploader'
import { UploadedFile } from './uploaded-file'

interface FileUploadProps {
  show: boolean
  isSingleFileMode?: boolean
  mimeTypes: string[]
  afterUpload: (file: DataFile) => void
  removeFile?: (fileId: number) => void
  onUploading: (value: boolean) => void
  uploading: boolean
  validateUploadFile?: (file: File, mimeType: string) => Promise<boolean>
  userFiles: DataFile[]
  removingFileId?: number
  shouldOptimize?: boolean
}

export const FileUploader = memo(
  ({
    afterUpload,
    mimeTypes,
    isSingleFileMode = false,
    show,
    validateUploadFile,
    removeFile,
    userFiles,
    removingFileId,
    onUploading,
    uploading,
    shouldOptimize = true,
  }: FileUploadProps) => {
    const { t } = useTypedTranslation(['client', 'common'])

    const {
      inputRef,
      optimize,
      setOptimize,
      errors,
      uploadedFiles,
      cancelUpload,
      uploadState,
      handleFiles,
      openFileSelection,
      setUploadedFiles,
      handleUpload,
      validate,
    } = useFileUploader({
      mimeTypes,
      afterUpload,
      onUploading,
      validateUploadFile,
      shouldOptimize,
    })
    const isAnyFileUploaded = useMemo(
      () => uploadedFiles.length > 0,
      [uploadedFiles.length],
    )
    const isUploadAvailable = useMemo(
      () => !(isAnyFileUploaded && isSingleFileMode),
      [isAnyFileUploaded, isSingleFileMode],
    )

    const handleFileDrop = async (monitor: DropTargetMonitor) => {
      const droppedFiles = monitor.getItem().files
      const file = droppedFiles[0]
      if (!validate(file)) {
        return
      }

      await handleUpload(file)
    }

    // biome-ignore lint/correctness/noUnusedVariables: <explanation>
    const [{ canDrop }, connectDrop] = useDrop({
      accept: '__NATIVE_FILE__',
      collect: monitor => ({
        canDrop: monitor.isOver({ shallow: true }) && monitor.canDrop(),
      }),
      drop: async (_, monitor) => {
        if (isUploadAvailable) {
          await handleFileDrop(monitor)
        }
      },
    })

    useEffect(() => {
      if (!uploading) {
        setUploadedFiles(prev =>
          prev.filter(file =>
            Object.values(userFiles)?.some(({ id }) => id === file.id),
          ),
        )
      }
    }, [userFiles, uploading])

    let readableMimeTypes: string | null
    if (mimeTypes === globalMimeTypes.all) {
      readableMimeTypes = null
    } else {
      readableMimeTypes = mimeTypes
        .map(type => type.split('/')[1])
        .filter(type => type !== '*')
        .join(', ')
    }

    return (
      <>
        {show && isUploadAvailable && (
          <>
            <div
              className={twJoin(
                'min-h-[88px] flex-col rounded-[6px] border border-border200 border-dashed',
                show ? 'flex' : 'hidden',
              )}
              onDragOver={e => {
                e.currentTarget.style.backgroundColor =
                  !isUploadAvailable || uploading
                    ? 'var(--color-btnDisabled)'
                    : 'var(--color-btnTertiary)'
              }}
              onDragLeave={e => {
                e.currentTarget.style.backgroundColor = ''
              }}
              onDrop={e => {
                e.currentTarget.style.backgroundColor = ''
              }}
            >
              <div
                ref={connectDrop}
                className="flex w-full flex-col items-center justify-center py-6"
              >
                <div className="mb-1 flex items-center justify-center gap-1 text-M text-textPrimary">
                  <span>{t('core.file_manager.upload.drag_file')}</span>
                  <TextButton
                    disabled={!isUploadAvailable}
                    onClick={openFileSelection}
                  >
                    {t('core.file_manager.upload.click_to_upload')}
                  </TextButton>
                </div>

                <div className="text-S text-textTertiary">
                  {readableMimeTypes ? (
                    <>
                      {t('core.file_manager.allowed_types')}
                      {`: ${readableMimeTypes}`}
                    </>
                  ) : (
                    t('core.file_manager.all_types_allowed')
                  )}
                </div>
                <input
                  style={{ display: 'none' }}
                  type="file"
                  accept={mimeTypes?.join(' ')}
                  ref={inputRef}
                  onChange={handleFiles}
                />
              </div>
              {errors.length > 0 &&
                errors.map(error => (
                  <div
                    key={error.slice(10)}
                    className="text-center text-red-500"
                  >
                    {error}
                  </div>
                ))}
            </div>
            {mimeTypes === globalMimeTypes.image && shouldOptimize && (
              <div className="mt-2.5 flex">
                <CheckboxNew
                  label="core.file_manager.upload.optimize.label"
                  onChange={() => setOptimize(!optimize)}
                  checked={optimize}
                  disabled={!isUploadAvailable}
                />
                <Tooltip
                  message={t('core.file_manager.upload.optimize.help_message')}
                />
              </div>
            )}
          </>
        )}
        {show &&
          isAnyFileUploaded &&
          uploadedFiles.map(file => (
            <div key={file.id} className={twJoin(!isSingleFileMode && 'mt-7')}>
              <UploadedFile
                currentFile={file}
                cancelUpload={cancelUpload}
                removeFile={removeFile}
                removingFileId={removingFileId}
                onRemovingChange={onUploading}
              />
            </div>
          ))}
        {show && !removingFileId && uploading && (
          <div className="mt-7">
            <UploadedFile
              cancelUpload={cancelUpload}
              progressInfo={uploadState}
            />
          </div>
        )}
      </>
    )
  },
)
